import React, {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useReducer,
    useRef,
} from 'react';
import { UseKeycloakService, KeycloakService } from '@europrocurement/l2d-keycloak';
import type {
    CountMessagesBodyResult,
    GetMessageBodyUnreadResult,
    GetMessagesBodyResult,
    IsConnectedBodyResult,
    MessageItem,
    MessageReadPayload,
    MessageResponse,
    NewMessageBody,
    TopicType,
    UnreadResponse,
    WebsocketPayloadCountMessages,
    WebsocketPayloadDeleteMessage,
    WebsocketPayloadEditMessage,
    WebsocketPayloadGetUnreadMessages,
    WebsocketPayloadGetUnreadNotifications,
    WebsocketPayloadIsConnected,
    WebsocketPayloadListMessage,
    WebsocketPayloadMessage,
    WebsocketPayloadSetMessagesRead,
    GetNotificationBodyUnreadResult,
    WebSocketPayloadGetLastMessages,
} from './WebsocketContext';
import { useSessionTimeout } from '../hook/useSessionTimeout';
import { sendAndWaitForResponse } from '../utils/websocketUtils';

export type WebsocketProviderType = {
    baseWebsocketUrl: string;
    children: ReactNode;
};

type WebsocketState = {
    socketOpen: boolean;
    messagesByTopic: Record<string, MessageItem[]>;
    messagesUnread: MessageItem[];
    lastMessages: MessageItem[];
    itemsUnread: UnreadResponse | null;
    unreadCountByTopic: Record<string, number>;
    isConnectedByUserIdentifier: Record<string, boolean>;
    errors: Record<string, unknown>;
};

type SendablePayload =
    | (WebsocketPayloadMessage & { action: string })
    | (WebsocketPayloadEditMessage & { action: string })
    | (WebsocketPayloadDeleteMessage & { action: string })
    | (WebsocketPayloadListMessage & { action: string })
    | (WebsocketPayloadCountMessages & { action: string })
    | (WebsocketPayloadIsConnected & { action: string })
    | (WebsocketPayloadSetMessagesRead & { action: string })
    | (WebsocketPayloadGetUnreadMessages & { action: string })
    | (WebsocketPayloadGetUnreadNotifications & { action: string })
    | (WebSocketPayloadGetLastMessages & { action: string });

type WebsocketActionsContextValue = {
    sendOnSocket: (payload: SendablePayload, actionExpected: string) => Promise<MessageResponse>;
    getSocket: () => WebSocket | null;
    hasSocket: () => boolean;
    fetchMessages: (
        topicId: string,
        topicType?: TopicType,
        from?: number,
        size?: number,
        query?: string,
    ) => Promise<void>;
    sendMessage: (payload: WebsocketPayloadMessage) => Promise<void>;
    updateMessage: (payload: WebsocketPayloadEditMessage) => Promise<void>;
    deleteMessage: (payload: WebsocketPayloadDeleteMessage) => Promise<void>;
    getMessageCount: (topicId: string) => Promise<void>;
    setMessagesRead: (messageRead: MessageReadPayload) => Promise<void>;
    getIsConnected: (userIdentifier: string) => Promise<void>;
    getUnreadMessages: (topicType?: TopicType) => Promise<void>;
    getUnreadItems: (contactId?: number) => Promise<void>;
    registerContactId: (contactId: number | undefined) => void;
    getError: () => Record<string, unknown>;
    getLastMessages: (topicType?: TopicType, size?: number) => Promise<void>;
};

export const SOCKET_OPEN = 'SOCKET_OPENED';
export const SOCKET_CLOSE = 'SOCKET_CLOSED';
export const SOCKET_ADD_MESSAGE = 'ADD_MESSAGE';
export const SOCKET_UPDATE_MESSAGE = 'UPDATE_MESSAGE';
export const SOCKET_DELETE_MESSAGE = 'DELETE_MESSAGE';
export const SOCKET_SET_TOPIC_MESSAGES = 'SET_TOPIC_MESSAGES';
export const SOCKET_SET_UNREAD_COUNT = 'SET_UNREAD_COUNT';
export const SOCKET_SET_IS_CONNECTED = 'SET_IS_CONNECTED';
export const SOCKET_SET_UNREAD_MESSAGES = 'SET_UNREAD_MESSAGES';
export const SOCKET_SET_UNREAD_NOTIFICATIONS = 'SET_UNREAD_NOTIFICATIONS';
export const SOCKET_SET_LAST_MESSAGES = 'SET_LAST_MESSAGES';
export const SOCKET_ADD_ERROR = 'ADD_ERROR';

type WebsocketAction =
    | { type: 'SOCKET_OPENED' }
    | { type: 'SOCKET_CLOSED' }
    | { type: 'ADD_MESSAGE'; payload: NewMessageBody }
    | { type: 'UPDATE_MESSAGE'; payload: NewMessageBody }
    | { type: 'DELETE_MESSAGE'; payload: NewMessageBody }
    | { type: 'SET_TOPIC_MESSAGES'; payload: GetMessagesBodyResult }
    | { type: 'SET_UNREAD_COUNT'; payload: CountMessagesBodyResult }
    | { type: 'SET_IS_CONNECTED'; payload: IsConnectedBodyResult }
    | { type: 'SET_UNREAD_MESSAGES'; payload: GetMessageBodyUnreadResult }
    | { type: 'SET_UNREAD_NOTIFICATIONS'; payload: GetNotificationBodyUnreadResult }
    | { type: 'SET_LAST_MESSAGES'; payload: GetMessagesBodyResult }
    | { type: 'ADD_ERROR'; payload: { key: string; error: unknown } };

const WebsocketStateContext = createContext<WebsocketState | null>(null);
const WebsocketActionsContext = createContext<WebsocketActionsContextValue | null>(null);

const initialState: WebsocketState = {
    socketOpen: false,
    messagesByTopic: {},
    messagesUnread: [],
    lastMessages: [],
    itemsUnread: null,
    unreadCountByTopic: {},
    isConnectedByUserIdentifier: {},
    errors: {},
};

const logwebsocket = false;

const isTokenValid = (kc: KeycloakService): boolean => {
    const exp = kc.getTokenParsed()?.exp;
    return !!exp && exp * 1000 > Date.now();
};

const websocketReducer = (state: WebsocketState, action: WebsocketAction): WebsocketState => {
    switch (action.type) {
        case SOCKET_OPEN:
            return {
                ...state,
                socketOpen: true,
            };

        case SOCKET_CLOSE:
            return {
                ...state,
                socketOpen: false,
            };

        case SOCKET_ADD_MESSAGE: {
            const { topicId } = action.payload.message.data;

            return {
                ...state,
                messagesByTopic: {
                    ...state.messagesByTopic,
                    [topicId]: [...(state.messagesByTopic[topicId] || []), action.payload.message],
                },
            };
        }

        case SOCKET_UPDATE_MESSAGE: {
            const { topicId } = action.payload.message.data;
            const currentMessages = state.messagesByTopic[topicId] || [];

            const updatedMessages = currentMessages.map((msg) =>
                msg.messageId === action.payload.message.messageId ? action.payload.message : msg,
            );

            return {
                ...state,
                messagesByTopic: {
                    ...state.messagesByTopic,
                    [topicId]: updatedMessages,
                },
            };
        }

        case SOCKET_DELETE_MESSAGE: {
            const { topicId } = action.payload.message.data;
            const currentMessages = state.messagesByTopic[topicId] || [];

            return {
                ...state,
                messagesByTopic: {
                    ...state.messagesByTopic,
                    [topicId]: currentMessages.filter(
                        (msg) => msg.messageId !== action.payload.message.messageId,
                    ),
                },
            };
        }

        case SOCKET_SET_TOPIC_MESSAGES: {
            const firstTopicId =
                action.payload.messages.length > 0 ? action.payload.messages[0].data.topicId : null;

            if (!firstTopicId) {
                return state;
            }

            return {
                ...state,
                messagesByTopic: {
                    ...state.messagesByTopic,
                    [firstTopicId]: action.payload.messages,
                },
            };
        }

        case SOCKET_SET_UNREAD_COUNT:
            return {
                ...state,
                unreadCountByTopic: {
                    ...state.unreadCountByTopic,
                    [action.payload.topicId]: action.payload.count,
                },
            };

        case SOCKET_SET_IS_CONNECTED:
            return {
                ...state,
                isConnectedByUserIdentifier: {
                    ...state.isConnectedByUserIdentifier,
                    [action.payload.userIdentifier]: action.payload.isConnected,
                },
            };

        case SOCKET_SET_UNREAD_MESSAGES:
            return {
                ...state,
                messagesUnread: action.payload.messages.filter(
                    (item) => item.data.topicType === 'messagerie_dossier',
                ),
            };

        case SOCKET_SET_UNREAD_NOTIFICATIONS: {
            return {
                ...state,
                itemsUnread: action.payload.messages ?? null,
            };
        }

        case SOCKET_SET_LAST_MESSAGES:
            return {
                ...state,
                lastMessages: action.payload.messages,
            };

        case SOCKET_ADD_ERROR:
            return {
                ...state,
                errors: {
                    ...state.errors,
                    [action.payload.key]: action.payload.error,
                },
            };

        default: {
            const exhaustiveCheck: never = action;
            throw new Error(`Unknown action: ${JSON.stringify(exhaustiveCheck)}`);
        }
    }
};

export const WebsocketProvider: React.FunctionComponent<WebsocketProviderType> = function ({
    baseWebsocketUrl,
    children,
}) {
    const socketRef = useRef<WebSocket | null>(null);
    const getUnreadItemsRef = useRef<(contactId?: number) => Promise<void>>(() =>
        Promise.resolve(),
    );
    const contactIdRef = useRef<number | undefined>(undefined);
    const kc = UseKeycloakService();
    const keycloakInstance = kc.getKc();

    const [state, dispatch] = useReducer(websocketReducer, initialState);

    const pushError = useCallback((error: unknown) => {
        dispatch({
            type: 'ADD_ERROR',
            payload: {
                key: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
                error,
            },
        });
    }, []);

    const sendOnSocket = useCallback(
        (payload: SendablePayload, actionExpected: string): Promise<MessageResponse> => {
            const socket = socketRef.current;

            if (!socket || socket.readyState !== WebSocket.OPEN) {
                return Promise.reject(new Error('Socket not open'));
            }

            return sendAndWaitForResponse<MessageResponse>(socket, payload, actionExpected);
        },
        [],
    );

    const createSocket = useCallback(() => {
        if (socketRef.current || !isTokenValid(kc)) {
            return;
        }

        const userIdentifier = kc.getTokenParsed()?.preferred_username || '';
        const url = `${baseWebsocketUrl}?userIdentifier=${encodeURIComponent(userIdentifier)}`;
        const socket = new WebSocket(url);

        if (logwebsocket) {
            console.debug(`[WS] Creating socket for ${userIdentifier} at ${url}`);
        }

        socket.onopen = () => {
            if (logwebsocket) {
                console.debug(`[WS] Opened at ${url}`);
            }

            dispatch({ type: 'SOCKET_OPENED' });
        };

        socket.onmessage = (event) => {
            if (logwebsocket) {
                console.debug('[WS] - onmessage event : ', event);
            }

            const data: MessageResponse = JSON.parse(event.data);

            try {
                const payload = JSON.parse(data.body);

                if (logwebsocket) {
                    console.debug('payload reçu', payload);
                }

                if (payload.action === 'newMessage') {
                    dispatch({ type: 'ADD_MESSAGE', payload: payload as NewMessageBody });
                    getUnreadItemsRef.current(contactIdRef.current);
                }

                if (payload.action === 'updateMesssage') {
                    dispatch({ type: 'UPDATE_MESSAGE', payload: payload as NewMessageBody });
                    getUnreadItemsRef.current(contactIdRef.current);
                }

                if (payload.action === 'deleteMesssage') {
                    dispatch({ type: 'DELETE_MESSAGE', payload: payload as NewMessageBody });
                    getUnreadItemsRef.current(contactIdRef.current);
                }

                if (payload.action === 'getMessages') {
                    dispatch({
                        type: 'SET_TOPIC_MESSAGES',
                        payload: payload as GetMessagesBodyResult,
                    });
                }

                if (payload.action === 'getMessagesCount') {
                    dispatch({
                        type: 'SET_UNREAD_COUNT',
                        payload: payload as CountMessagesBodyResult,
                    });
                }

                if (payload.action === 'isconnected') {
                    dispatch({
                        type: 'SET_IS_CONNECTED',
                        payload: payload as IsConnectedBodyResult,
                    });
                }

                if (payload.action === 'getUnreadMessages') {
                    dispatch({
                        type: 'SET_UNREAD_MESSAGES',
                        payload: payload as GetMessageBodyUnreadResult,
                    });
                }

                if (payload.action === 'getUnreadNotifications') {
                    dispatch({
                        type: 'SET_UNREAD_NOTIFICATIONS',
                        payload: payload as GetNotificationBodyUnreadResult,
                    });
                }
                if (payload.action === 'getLastMessages') {
                    dispatch({
                        type: 'SET_LAST_MESSAGES',
                        payload: payload as GetMessagesBodyResult,
                    });
                }
            } catch (error) {
                if (logwebsocket) {
                    console.error('[WS] Invalid message payload', error);
                }
                pushError(error);
            }
        };

        socket.onclose = (event) => {
            if (logwebsocket) {
                console.debug('[WS] - onclose event : ', event);
            }

            dispatch({ type: 'SOCKET_CLOSED' });
            socketRef.current = null;

            if (logwebsocket) {
                console.debug(`[WS] Closed ${url}`, {
                    code: event.code,
                    reason: event.reason,
                    wasClean: event.wasClean,
                });
            }
        };

        socket.onerror = (error) => {
            if (logwebsocket) {
                console.error('[WS] Error', error);
            }
            pushError(error);
        };

        socketRef.current = socket;
    }, [baseWebsocketUrl, kc, pushError]);

    const getSocket = useCallback((): WebSocket | null => socketRef.current ?? null, []);

    const hasSocket = useCallback((): boolean => !!socketRef.current, []);

    const waitForSocket = useCallback(
        (timeoutMs = 5000): Promise<void> =>
            new Promise((resolve, reject) => {
                if (socketRef.current?.readyState === WebSocket.OPEN) {
                    resolve();
                    return;
                }
                createSocket();
                const start = Date.now();
                const interval = setInterval(() => {
                    if (socketRef.current?.readyState === WebSocket.OPEN) {
                        clearInterval(interval);
                        resolve();
                    } else if (Date.now() - start >= timeoutMs) {
                        clearInterval(interval);
                        reject(new Error('WebSocket connection timeout'));
                    }
                }, 100);
            }),
        [createSocket],
    );

    const withRetry = useCallback(
        async <T,>(thunk: () => Promise<T>): Promise<T> => {
            try {
                return await thunk();
            } catch {
                await waitForSocket();
                await keycloakInstance.updateToken(0);
                return thunk();
            }
        },
        [keycloakInstance, waitForSocket],
    );

    const sendMessage = useCallback(
        async (payload: WebsocketPayloadMessage): Promise<void> => {
            try {
                await withRetry(() =>
                    sendOnSocket({ ...payload, action: 'sendmessage' }, 'sendmessage'),
                );
            } catch (error) {
                if (logwebsocket) {
                    console.error(`[WS] Failed to send a message with payload`, payload, error);
                }
                pushError(error);
            }
        },
        [sendOnSocket, pushError, withRetry],
    );

    const updateMessage = useCallback(
        async (payload: WebsocketPayloadEditMessage): Promise<void> => {
            try {
                await withRetry(() =>
                    sendOnSocket({ ...payload, action: 'updatemessage' }, 'updatemessage'),
                );
            } catch (error) {
                if (logwebsocket) {
                    console.error(`[WS] Failed to update a message with payload`, payload, error);
                }
                pushError(error);
            }
        },
        [sendOnSocket, pushError, withRetry],
    );

    const deleteMessage = useCallback(
        async (payload: WebsocketPayloadDeleteMessage): Promise<void> => {
            try {
                await withRetry(() =>
                    sendOnSocket({ ...payload, action: 'deletemessage' }, 'deletemessage'),
                );
            } catch (error) {
                if (logwebsocket) {
                    console.error(`[WS] Failed to delete a message with payload`, payload, error);
                }
                pushError(error);
            }
        },
        [sendOnSocket, pushError, withRetry],
    );

    const getIsConnected = useCallback(
        async (userIdentifier: string): Promise<void> => {
            try {
                await withRetry(async () => {
                    const kcToken = await keycloakInstance
                        .updateToken(10)
                        .then(() => keycloakInstance.token || '');
                    const payload: WebsocketPayloadIsConnected = {
                        payload: { token: kcToken, userIdentifier },
                    };
                    await sendOnSocket({ ...payload, action: 'isconnected' }, 'isconnected');
                });
            } catch (error) {
                if (logwebsocket) {
                    console.error(`[WS] Failed to get is connected for ${userIdentifier}`, error);
                }
                pushError(error);
            }
        },
        [keycloakInstance, sendOnSocket, pushError, withRetry],
    );

    const getMessageCount = useCallback(
        async (topicId: string): Promise<void> => {
            try {
                await withRetry(async () => {
                    const kcToken = await keycloakInstance
                        .updateToken(10)
                        .then(() => keycloakInstance.token || '');
                    const payload: WebsocketPayloadCountMessages = {
                        payload: { token: kcToken, topicId },
                    };
                    await sendOnSocket({ ...payload, action: 'countmessage' }, 'countmessages');
                });
            } catch (error) {
                if (logwebsocket) {
                    console.error(`[WS] Failed to get count messages for ${topicId}`, error);
                }
                pushError(error);
            }
        },
        [keycloakInstance, sendOnSocket, pushError, withRetry],
    );

    const getUnreadMessages = useCallback(
        async (topicType?: TopicType): Promise<void> => {
            try {
                await withRetry(async () => {
                    const kcToken = kc.getKc()?.token ?? '';
                    const payload: WebsocketPayloadGetUnreadMessages = {
                        payload: { token: kcToken, topicType },
                    };
                    await sendOnSocket(
                        { ...payload, action: 'getunreadmessages' },
                        'getunreadmessages',
                    );
                });
            } catch (error) {
                console.error(`[WS] Failed to get unread messages for ${topicType}`, error);
                pushError(error);
            }
        },
        [kc, sendOnSocket, pushError, withRetry],
    );

    const getLastMessages = useCallback(
        async (topicType?: TopicType, size?: number): Promise<void> => {
            try {
                await withRetry(async () => {
                    const kcToken = await keycloakInstance
                        .updateToken(10)
                        .then(() => keycloakInstance.token || '');

                    const payload: WebSocketPayloadGetLastMessages = {
                        payload: { token: kcToken, topicType, size },
                    };
                    if (size === undefined) {
                        delete payload.payload.size;
                    }
                    if (topicType === undefined) {
                        delete payload.payload.topicType;
                    }
                    await sendOnSocket(
                        { ...payload, action: 'getlastmessages' },
                        'getlastmessages',
                    );
                });
            } catch (error) {
                if (logwebsocket) {
                    console.error(`[WS] Failed to get last messages for ${topicType}`, error);
                }
                pushError(error);
            }
        },
        [keycloakInstance, sendOnSocket, pushError, withRetry],
    );

    const setMessagesRead = useCallback(
        async (messageRead: MessageReadPayload): Promise<void> => {
            try {
                await withRetry(async () => {
                    const kcToken = await keycloakInstance
                        .updateToken(10)
                        .then(() => keycloakInstance.token || '');
                    const payload: WebsocketPayloadSetMessagesRead = {
                        payload: {
                            token: kcToken,
                            messagesIds: messageRead.messagesIds,
                            topicType: messageRead.topicType,
                            topicId: messageRead.topicId,
                        },
                    };
                    await sendOnSocket(
                        { ...payload, action: 'setmessagesread' },
                        'setmessagesread',
                    );
                });
            } catch (error) {
                if (logwebsocket) {
                    console.error(
                        `[WS] Failed to set messages read for ${messageRead.messagesIds}`,
                        error,
                    );
                }
                pushError(error);
            }
        },
        [keycloakInstance, sendOnSocket, pushError, withRetry],
    );

    const fetchMessages = useCallback(
        async (
            topicId: string,
            topicType?: TopicType,
            from?: number,
            size?: number,
            query?: string,
        ): Promise<void> => {
            try {
                await withRetry(async () => {
                    const kcToken = await keycloakInstance
                        .updateToken(10)
                        .then(() => keycloakInstance.token || '');
                    const userIdentifier = kc.getTokenParsed()?.preferred_username || '';

                    let searchParams: Record<string, unknown> = {};
                    if (from !== undefined) searchParams = { ...searchParams, from };
                    if (size !== undefined) searchParams = { ...searchParams, size };
                    if (topicType) searchParams = { ...searchParams, topicType };
                    if (query) searchParams = { ...searchParams, query };

                    const payload: WebsocketPayloadListMessage = {
                        payload: { token: kcToken, topicId, userIdentifier, ...searchParams },
                    };
                    await sendOnSocket({ ...payload, action: 'getmessages' }, 'getmessages');
                });
            } catch (error) {
                if (logwebsocket) {
                    console.error(`[WS] Failed to fetch messages for ${topicId}`, error);
                }
                pushError(error);
            }
        },
        [kc, keycloakInstance, sendOnSocket, pushError, withRetry],
    );

    const getUnreadItems = useCallback(
        async (contactId?: number): Promise<void> => {
            try {
                await withRetry(async () => {
                    const kcToken = kc.getKc()?.token ?? '';
                    const payload: WebsocketPayloadGetUnreadNotifications = {
                        payload: { token: kcToken, contactId },
                    };
                    await sendOnSocket(
                        { ...payload, action: 'getunreadnotifications' },
                        'getunreadnotifications',
                    );
                });
            } catch (error) {
                console.error(`[WS] Failed to get unread notifications.`, error);
                pushError(error);
            }
        },
        [kc, sendOnSocket, pushError, withRetry],
    );

    getUnreadItemsRef.current = getUnreadItems;

    const registerContactId = useCallback((id: number | undefined) => {
        contactIdRef.current = id;
    }, []);

    useEffect(() => {
        if (!state.socketOpen && !socketRef.current) {
            if (logwebsocket) {
                console.debug('Socket fermé, tentative de reconnexion...');
                console.debug('val:', state.socketOpen, socketRef.current, isTokenValid(kc));
            }

            createSocket();
        }
    }, [state.socketOpen, createSocket, kc]);

    useSessionTimeout(kc, () => {});

    const actionsValue = useMemo<WebsocketActionsContextValue>(
        () => ({
            sendOnSocket,
            getSocket,
            hasSocket,
            fetchMessages,
            sendMessage,
            updateMessage,
            deleteMessage,
            getMessageCount,
            setMessagesRead,
            getIsConnected,
            getUnreadMessages,
            getUnreadItems,
            registerContactId,
            getError: () => state.errors,
            getLastMessages,
        }),
        [
            sendOnSocket,
            getSocket,
            hasSocket,
            fetchMessages,
            sendMessage,
            updateMessage,
            deleteMessage,
            getMessageCount,
            setMessagesRead,
            getIsConnected,
            getUnreadMessages,
            getUnreadItems,
            registerContactId,
            state.errors,
            getLastMessages,
        ],
    );

    return (
        <WebsocketStateContext.Provider value={state}>
            <WebsocketActionsContext.Provider value={actionsValue}>
                {children}
            </WebsocketActionsContext.Provider>
        </WebsocketStateContext.Provider>
    );
};

export const useWebsocketState = (): WebsocketState => {
    const context = useContext(WebsocketStateContext);

    if (!context) {
        throw new Error('useWebsocketState must be used within a WebsocketProvider');
    }

    return context;
};

export const useWebsocketActions = (): WebsocketActionsContextValue => {
    const context = useContext(WebsocketActionsContext);

    if (!context) {
        throw new Error('useWebsocketActions must be used within a WebsocketProvider');
    }

    return context;
};

export const useWebsocketCtx = () => {
    const state = useWebsocketState();
    const actions = useWebsocketActions();

    return {
        baseWebsocketUrl: undefined,
        ...state,
        ...actions,
    };
};

export { WebsocketStateContext, WebsocketActionsContext };
